<template>
    <div class="form-content">
        <KFormFieldDisplay
            name="huvudoperator"
            label="Huvudoperatör"
            :showEmptyValue="true"
            :value="huvudoperator"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="bokad_operation_datum"
            label="Operationsdatum"
            :value="bokad_operation_datum"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="typoperation"
            label="Typoperation"
            :value="typoperation"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="huvuddiagnos"
            label="Huvuddiagnos"
            class="span-two"
            :value="huvuddiagnos"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="huvudoperationskod_display"
            label="Huvudoperationskod"
            class="span-two"
            :value="huvudoperationskod"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="bidiagnoser_display"
            label="Bidiagnoser"
            contentDirection="vertical"
            :value="bidiagnoser"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="bioperationskoder"
            label="Bioperationskod"
            contentDirection="vertical"
            :value="bioperationskoder"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <KFormFieldDisplay
            name="niva_fran_till"
            label="Nivå från - till"
            :value="niva_fran_till_display"
            #default="{ value }"
            :labelWeight="labelWeight"
            :margin="margin"
        >
            <NivaFranTill :data="value" />
        </KFormFieldDisplay>

        <KFormFieldDisplay
            name="digitum"
            label="Digitum/Digitus"
            contentDirection="vertical"
            :value="digitum_display"
            :labelWeight="labelWeight"
            :margin="margin"
        />

        <div class="grid-right">
            <KButton
                variant="link-background-white"
                @click.prevent="emit('openAnmalan')"
                class="operationsinformation-button"
            >
                Visa operationsinformation
            </KButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KButton from "@/components/Button.vue";
    import NivaFranTill from "@/tabs/Operation/NivaFranTill.vue";
    import { computed, provide, toRefs } from "vue";

    const props = withDefaults(
        defineProps<{
            anmalan: any;
            /**
             * Ska tomt värde visas? Visning av tomt värde innebär att "-" visas. False leder till att hela fältet döljs
             * vid tomt värde
             */
            showEmptyValues?: boolean;
            /**
             * Font weight på label
             */
            labelWeight?: "default" | "bold";
            /**
             * Mängd marginal mellan label och content
             */
            margin?: "default" | "small" | "large";
        }>(),
        {
            showEmptyValues: undefined,
        }
    );

    const emit = defineEmits(["openAnmalan"]);

    const {
        current_bokad_huvudoperator_display,
        bokad_operation_datum,
        typoperation_display,
        huvuddiagnos_display,
        huvudoperationskod_display,
        bidiagnoser_display,
        bioperationskoder_display,
        digitum_display,
        niva_fran_till_display,
        huvudoperator_ej_bestamd,
    } = toRefs(props.anmalan) || {};

    const { labelWeight, margin, showEmptyValues } = toRefs(props);

    provide("showEmptyValues", showEmptyValues.value);

    const huvudoperator = computed(() =>
        huvudoperator_ej_bestamd.value
            ? "Huvudoperatör ej bestämd"
            : current_bokad_huvudoperator_display.value?.user_display?.name
    );

    const typoperation = computed(() => typoperation_display.value?.namn);
    const huvuddiagnos = computed(
        () => `${huvuddiagnos_display.value?.code} - ${huvuddiagnos_display.value?.displayName}`
    );
    const bidiagnoser = computed(
        () => bidiagnoser_display.value?.map((item: any) => `${item?.code} - ${item?.displayName}`) || []
    );
    const huvudoperationskod = computed(
        () => `${huvudoperationskod_display.value?.code} - ${huvudoperationskod_display.value?.displayName}`
    );

    const bioperationskoder = computed(
        () => bioperationskoder_display.value?.map((item: any) => `${item?.code} - ${item?.displayName}`) || []
    );
</script>

<style lang="scss" scoped>
    @import "@/style/form";
    @import "@/style/utilitary";

    .operation-section {
        .form-content {
            background-color: $form-display-background;
            padding: 1.375rem;
        }
    }

    @media print {
        .form-content {
            padding: 0 !important;
        }
    }
</style>
